import React, { useMemo } from 'react'
import { createStructuredSelector } from 'reselect'
import { connect } from 'react-redux'
import { AuditDetail, NegotiationHistorySubscriptionIdentifier, NegotiationStatus, UserPreference } from 'api'
import { Locale, Translation } from 'locale'
import { WindowActions } from 'epics/windowEpic'
import {
  BannerCurrencyTile,
  BannerStatusTile,
  BannerTilesGroup,
  DetailTilesContainer,
  EnvironmentTitle,
  getNegotiationByStatus,
  getNegotiationCashAmountsForStatus,
  HeroPanel,
  NegotiationsByStatus,
  TradeAwayEnum
} from 'view/shared/heroPanel'
import { completedNegotiationsCollectionNameSelector } from 'selectors/collectionSelectors'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { isCurrentOrganisationSellSideSelector } from 'selectors/organisationSelectors'
import * as constants from 'view/styles/constants'
import { createNegotiationHistoryDetailsSelector } from 'selectors/auditSelectors'

interface CompletedNegotiationsHeroPanelProps {
  completedNegotiations: AuditDetail[]
  collectionName: string | null
  userPreference: UserPreference
  isSellSideOrganisation: boolean
  onTearout: () => void
}

const mapStateToProps = createStructuredSelector({
  completedNegotiations: createNegotiationHistoryDetailsSelector([NegotiationHistorySubscriptionIdentifier.HISTORICAL]),
  collectionName: completedNegotiationsCollectionNameSelector,
  isSellSideOrganisation: isCurrentOrganisationSellSideSelector,
  userPreference: createUserPreferenceSelector()
})

const mapDispatchToProps = {
  onTearout: () => WindowActions.tearoutGrid('/quote/tearout/rfqs/historical')
}

const CompletedNegotiationsHeroPanelComponent: React.FunctionComponent<CompletedNegotiationsHeroPanelProps> = props => {
  const { completedNegotiations, collectionName, onTearout, isSellSideOrganisation, userPreference } = props

  const title = useMemo(
    () => `${Locale.translate(Translation.TC_HERO_HISTORICAL)} - ${collectionName || Locale.translate(Translation.TC_HERO_ALL)}`,
    [collectionName]
  )

  const negotiationsByStatus: Partial<NegotiationsByStatus> = useMemo(
    () => getNegotiationByStatus(completedNegotiations, isSellSideOrganisation),
    [completedNegotiations, isSellSideOrganisation]
  )

  const cashAmounts = useMemo(() => getNegotiationCashAmountsForStatus(completedNegotiations, NegotiationStatus.ACCEPTED), [
    completedNegotiations
  ])

  return (
    <HeroPanel onTearout={onTearout}>
      <DetailTilesContainer>
        <EnvironmentTitle data-test-id={'CompletedNegotiationsHeroPanel-EnvironmentTitle'}>{title}</EnvironmentTitle>
        <BannerTilesGroup title={Locale.translate(Translation.TC_HERO_STATUS)}>
          <BannerStatusTile
            title={Locale.translate(Translation.TC_ACCEPTED)}
            label={negotiationsByStatus[NegotiationStatus.ACCEPTED] || 0}
            statusColor={constants.STATUS_COLOR_ACCEPTED}
          />
          <BannerStatusTile
            title={Locale.translate(Translation.TC_REJECTED)}
            label={negotiationsByStatus[NegotiationStatus.REJECTED] || 0}
            statusColor={constants.STATUS_COLOR_URGENT}
          />
          {isSellSideOrganisation && (
            <>
              <BannerStatusTile
                title={Locale.translate(Translation.TC_MISSED)}
                label={negotiationsByStatus[NegotiationStatus.MISSED] || 0}
                statusColor={constants.STATUS_COLOR_DEFAULT}
              />
              <BannerStatusTile
                title={Locale.translate(Translation.TC_TRADED_AWAY)}
                label={negotiationsByStatus[TradeAwayEnum.TRADED_AWAY] || 0}
                statusColor={constants.STATUS_COLOR_DEFAULT}
              />
            </>
          )}
          <BannerStatusTile
            title={Locale.translate(Translation.TC_EXPIRED)}
            label={negotiationsByStatus[NegotiationStatus.EXPIRED] || 0}
            statusColor={constants.STATUS_COLOR_URGENT}
          />
          <BannerStatusTile
            title={Locale.translate(Translation.TC_CANCELLED)}
            label={negotiationsByStatus[NegotiationStatus.CANCELLED] || 0}
            statusColor={constants.STATUS_COLOR_DEFAULT}
          />
        </BannerTilesGroup>
      </DetailTilesContainer>
      {cashAmounts.length > 0 && (
        <DetailTilesContainer large={true}>
          <BannerTilesGroup title={Locale.translate(Translation.TC_HERO_CASH_AMOUNTS)}>
            {cashAmounts.map(cashAmount => (
              <BannerCurrencyTile key={cashAmount.currency} cashAmount={cashAmount} userPreference={userPreference} />
            ))}
          </BannerTilesGroup>
        </DetailTilesContainer>
      )}
    </HeroPanel>
  )
}

export const CompletedNegotiationsHeroPanel = connect(mapStateToProps, mapDispatchToProps)(CompletedNegotiationsHeroPanelComponent)
